import React, { useEffect } from 'react'
import { useSpring, animated } from '@react-spring/web'
import { X } from 'lucide-react'
import { cn } from '@/utils/cn'

interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  hideClose?: boolean
  closeOnOverlayClick?: boolean
  contentClassName?: string
}

const sizeClasses = {
  sm: 'max-w-[min(420px,96vw)]',
  md: 'max-w-[min(560px,96vw)]',
  lg: 'max-w-[min(720px,96vw)]',
  xl: 'max-w-[min(920px,96vw)]',
  '2xl': 'max-w-[min(1120px,96vw)]',
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  hideClose = false,
  closeOnOverlayClick = true,
  contentClassName,
}: ModalProps) {
  const backdropSpring = useSpring({
    opacity: open ? 1 : 0,
    config: { tension: 280, friction: 24 },
  })

  const modalSpring = useSpring({
    opacity: open ? 1 : 0,
    transform: open ? 'scale(1) translateY(0px)' : 'scale(0.94) translateY(16px)',
    config: { tension: 320, friction: 26 },
  })

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  if (!open) return null

  return (
    <animated.div
      style={{ ...backdropSpring, pointerEvents: open ? 'auto' : 'none' }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5"
      onClick={closeOnOverlayClick ? onClose : undefined}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      <animated.div
        style={modalSpring}
        className={cn(
          'relative z-10 w-full max-h-[calc(100vh-1.5rem)] overflow-hidden glass-modal sm:max-h-[calc(100vh-2.5rem)]',
          sizeClasses[size],
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {/* Header */}
        {(title || !hideClose) && (
          <div className="sticky top-0 z-10 flex items-start justify-between border-b border-[var(--border-subtle)] bg-[var(--glass-strong)] px-6 py-5 backdrop-blur-xl">
            <div>
              {title && (
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-[var(--text-primary)]"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                  {description}
                </p>
              )}
            </div>
            {!hideClose && (
              <button
                onClick={onClose}
                className={cn(
                  'ml-4 flex items-center justify-center w-8 h-8 rounded-[var(--radius-sm)]',
                  'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                  'hover:bg-[var(--glass-medium)] transition-colors duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
                )}
                aria-label="Fechar modal"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'overflow-y-auto px-6 pb-6 pr-4',
            !title && !hideClose ? 'pt-6' : 'pt-5',
            title || !hideClose ? 'max-h-[calc(100vh-9rem)]' : 'max-h-[calc(100vh-4rem)]',
            contentClassName,
          )}
        >
          {children}
        </div>
      </animated.div>
    </animated.div>
  )
}
